import _extends from "@babel/runtime/helpers/extends";
/*
 *                     NOTICE
 *            COPYRIGHT 2023 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */
import { Expression } from "abreact/expression/Expression";
import { ExecutionFlags } from "abreact/expression/ui/editor/impl/condition/operandValue/ExecutionFlags";
import { LocalValidationErrorIcon } from "abreact/expression/ui/editor/impl/condition/operandValue/operand/LocalValidationErrorIcon";
import { OperatorTree } from "abreact/expression/ui/editor/impl/condition/operandValue/operator/OperatorTree";
import * as AbReactResources from "abreact/generated/locale/AbReactResources";
import { PopupUtils } from "abreact/incubator/ui/popup/PopupUtils";
import { SelectionMode } from "abreact/incubator/ui/SharedTypes";
import { CssUtils } from "abreact/incubator/ui/utils/CssUtils";
import { AbDropdown } from "abreact/input/AbDropdown";
import { AbWithTooltip } from "abreact/popup/AbWithTooltip";
import { AbTreeRootNodeImpl } from "abreact/tree/node/AbTreeRootNodeImpl";
import { AbFilterUtils } from "abreact/utils/AbFilterUtils";
import { AbIterable } from "abweb/collection/AbIterable";
import { Assert } from "abweb/utils/Assert";
import Immutable from "immutable";
import * as React from "react";
// import OperatorElementOption = Expression.Domain.OperatorElementOption;
// import ValidatedExpOperator = Expression.Domain.ValidatedExpOperator;
// import OperandValue = GuidedExpressionEditor.AbstractModel.AbstractRowModel.OperandValue;

export function OperatorOperandValueEditor(props) {
  const {
    sourceOperandValue,
    operatorOperandValue,
    operatorElementOptions,
    expressionDomainModel,
    componentFactory,
    dataTestId
  } = props;
  const selectedExpOperator = operatorOperandValue?.expOperator && !expressionDomainModel.isPlaceholderExpOperator(operatorOperandValue.expOperator) ? operatorOperandValue.expOperator : undefined;
  const validExpOperators = operatorElementOptions.filter(({
    isValid
  }) => isValid).map(({
    expOperator
  }) => expOperator);
  const isSelectedExpOperatorValid = selectedExpOperator == null || validExpOperators.map(({
    id
  }) => id).includes(selectedExpOperator.id);
  const operatorTree = React.useMemo(() => new OperatorTree(selectedExpOperator, validExpOperators, isSelectedExpOperatorValid, expressionDomainModel), [selectedExpOperator, validExpOperators, isSelectedExpOperatorValid, expressionDomainModel]);
  const allowUnset = !props.isReadonly && props.allowUnset;
  const treeRootNode = React.useMemo(() => {
    const roots = [];
    if (allowUnset) {
      roots.push(OperatorTree.Path.unset(OperatorOperandValueEditor.unsetLabel()));
    }
    roots.push(...operatorTree.roots);
    return new AbTreeRootNodeImpl(roots, OperatorOperandValueEditor.keyFunction);
  }, [allowUnset, operatorTree]);
  function onPathChange(path) {
    if (allowUnset) {
      if (OperatorTree.Path.isNotUnset(path)) {
        props.onChange(OperatorTree.Path.getExpOperator(path), Immutable.Set() /* No flags selected */);
      } else {
        props.onChange(undefined);
      }
    } else {
      Assert.assert(!props.isReadonly);
      Assert.assert(OperatorTree.Path.isNotUnset(path));
      props.onChange(OperatorTree.Path.getExpOperator(path), Immutable.Set() /* No flags selected */);
    }
  }
  function onExecutionFlagsChanged(executionFlags) {
    Assert.assert(!props.isReadonly);
    Assert.assert(selectedExpOperator != null);
    props.onChange(selectedExpOperator, Immutable.OrderedSet(Array.from(executionFlags).sort()));
  }
  const positioner = React.useMemo(() => {
    const {
      spacing
    } = componentFactory;
    return spacing != null ? PopupUtils.createVerticalPositioner({
      spacing
    }) : undefined;
  }, [componentFactory]);
  const className = CssUtils.composeClassNames(OperatorOperandValueEditor.CLASS_NAME, !isSelectedExpOperatorValid && "invalid-selected-operator");
  const dropdownTreeProps = {
    className: className,
    popupParentClass: `${OperatorOperandValueEditor.CLASS_NAME}-contents`,
    dataTestId: `${dataTestId}-operatorInput`,
    placeholder: AbReactResources.expression_operator_value_prompt(),
    selectionMode: SelectionMode.SINGLE,
    roots: treeRootNode,
    keyFunction: OperatorOperandValueEditor.keyFunction,
    value: operatorTree.selectedValue,
    disabled: props.isReadonly,
    valueRenderer: OperatorOperandValueEditor.renderSelectedPath,
    positioner: positioner,
    onChange: onPathChange
  };
  const distinctOperatorIDs = React.useMemo(() => AbIterable.from([selectedExpOperator, ...validExpOperators]).filterType(expOperator => expOperator != null).map(({
    id
  }) => id).toSet(), [selectedExpOperator, validExpOperators]);
  const useFilteredDropdownTree = React.useMemo(() => {
    const {
      useFilteredOperatorDropdownTreeThresholdExclusive: threshold
    } = componentFactory;
    if (threshold === false) {
      return false;
    }
    return distinctOperatorIDs.size > threshold;
  }, [componentFactory, distinctOperatorIDs]);
  const dropdownTree = useFilteredDropdownTree ? /*#__PURE__*/React.createElement(AbDropdown.FilteredTree, _extends({}, dropdownTreeProps, {
    itemRenderer: AbFilterUtils.boldMatchRenderer
  })) : /*#__PURE__*/React.createElement(AbDropdown.Tree, dropdownTreeProps);
  const contents = /*#__PURE__*/React.createElement("div", {
    className: "ab-gee-operator-inputs"
  }, dropdownTree, operatorOperandValue != null && /*#__PURE__*/React.createElement(ExecutionFlags, {
    disabled: props.isReadonly,
    domain: expressionDomainModel,
    valueTypeIDs: Immutable.Set(sourceOperandValue?.valueTypeIDs ?? []),
    eligibleFlags: new Set(selectedExpOperator?.operatorExecutionFlags),
    selectedFlags: operatorOperandValue.executionFlags?.toArray() ?? [],
    onChange: onExecutionFlagsChanged
  }), !isSelectedExpOperatorValid && /*#__PURE__*/React.createElement(LocalValidationErrorIcon, {
    tooltip: componentFactory.renderExpOperatorLocalValidationErrorIconTooltip?.(sourceOperandValue, selectedExpOperator) ?? `Selected operator is invalid`
  }));
  if (selectedExpOperator?.description != null) {
    const tooltip = /*#__PURE__*/React.createElement("span", {
      "data-test-id": `${props.dataTestId}-selectedOperatorDescriptionTooltip`
    }, selectedExpOperator.description);
    return /*#__PURE__*/React.createElement(AbWithTooltip, {
      tooltip: tooltip
    }, contents);
  } else {
    return contents;
  }
}
(function (_OperatorOperandValueEditor) {
  const CLASS_NAME = _OperatorOperandValueEditor.CLASS_NAME = "expression-editor-operator-dropdown";
  function renderSelectedPath(path) {
    if (path != null && OperatorTree.Path.isNotUnset(path)) {
      const selectedExpOperator = OperatorTree.Path.getExpOperator(path);
      const displayName = Expression.Domain.Operator.getDisplayNameForSelectedValue(selectedExpOperator);
      return /*#__PURE__*/React.createElement("span", null, displayName);
    } else {
      return /*#__PURE__*/React.createElement("span", {
        "aria-label": unsetLabel()
      }, "$", unsetLabel());
    }
  }
  _OperatorOperandValueEditor.renderSelectedPath = renderSelectedPath;
  function unsetLabel() {
    return AbReactResources.expression_operator_none();
  }
  _OperatorOperandValueEditor.unsetLabel = unsetLabel;
  function keyFunction(pathElement) {
    return pathElement != null ? pathElement.id : unsetLabel();
  }
  _OperatorOperandValueEditor.keyFunction = keyFunction;
})(OperatorOperandValueEditor || (OperatorOperandValueEditor = {}));
//# sourceMappingURL=OperatorOperandValueEditor.js.map
